import { isPromotionWithinPeriod } from './engine/evaluate-conditions';
import {
  AppliedPromotion,
  BuyXPayYConfig,
  DiscountType,
  NthUnitConfig,
  ProgressiveConfig,
  PromotionInput,
  PromotionPeriod,
} from './promotion.types';

// NOTE: duplicated from utils/src/lib/types.ts to avoid a circular dependency
type SUPPORTED_LANGUAGE = 'es' | 'en';

export interface HeadlineProductVariant {
  price?: number | null;
  priceDiscount?: number | null;
}

export interface HeadlineProductInput {
  price?: number | null;
  priceDiscount?: number | null;
  presentationItems?: ReadonlyArray<{
    price?: number | null;
    priceDiscount?: number | null;
  }> | null;
}

export interface BuyXPayYOffer {
  buy: number;
  pay: number;
  averagePercentage: number;
  sourcePromotionId: string;
}

export interface ProgressiveTierOffer {
  quantity: number;
  percentage: number;
  sourcePromotionId: string;
}

export interface NthUnitOffer {
  nthUnit: number;
  discountPercentage: number;
  ordinal: string;
  sourcePromotionId: string;
}

export interface PromotionFamilyGrouping {
  buyXPayYOffers: BuyXPayYOffer[];
  progressiveTiers: ProgressiveTierOffer[];
  nthUnits: NthUnitOffer[];
}

const DISCOUNT_TYPE_ORDER: Record<DiscountType, number> = {
  buyXPayY: 0,
  nthUnit: 1,
  progressive: 2,
};

const SPANISH_FEMININE_ORDINAL_ABBREVIATIONS: Record<number, string> = {
  1: '1ra',
  2: '2da',
  3: '3ra',
  4: '4ta',
  5: '5ta',
  6: '6ta',
  7: '7ma',
  8: '8va',
  9: '9na',
  10: '10ma',
};

export function getPromotionI18nParams(
  promotion: PromotionInput,
  language: SUPPORTED_LANGUAGE = 'es',
): Record<string, unknown> {
  switch (promotion.discountType) {
    case 'buyXPayY': {
      const config = promotion.buyXPayYConfig;
      return { buy: config?.buy, pay: config?.pay };
    }
    case 'nthUnit': {
      const config = promotion.nthUnitConfig;
      const n = config?.nthUnit;
      return {
        n,
        ordinal: n != null ? formatFeminineOrdinal(n, language) : undefined,
        percentage: config?.discountPercentage,
      };
    }
    case 'progressive': {
      const tiers = promotion.progressiveConfig?.tiers;
      const percentage = tiers?.length ? Math.max(...tiers.map(t => t.percentage)) : 0;
      return { percentage };
    }
  }
}

/**
 * Single money-rounding convention shared by the cart engine and the upsell
 * hints: round to whole cents (2 decimals). Using it in both places guarantees
 * the total a hint anticipates ("ahorrás $X") matches what the cart shows once
 * the units are added — same value fed to the same currency pipe.
 */
export function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * Feminine ordinal abbreviation used with a feminine noun (e.g. "2da unidad" in Spanish).
 * It falls back to the symbolic ordinal form `Nº`
 */
export function formatFeminineOrdinal(n: number, language: SUPPORTED_LANGUAGE = 'es'): string {
  if (language === 'es') {
    const abbreviation = SPANISH_FEMININE_ORDINAL_ABBREVIATIONS[n];
    if (abbreviation) {
      return abbreviation;
    }
  }
  return `${n}°`;
}

/**
 * Splits a flat list of promotions into the three display families (buy-X-pay-Y, progressive, n-th unit).
 * The returned arrays are sorted to give a stable display order:
 *   - buy-X-pay-Y offers by `buy` asc
 *   - progressive tiers by `quantity` asc
 *   - n-th units by `nthUnit` asc
 */
export function groupPromotionsByFamily(
  promotions: PromotionInput[],
  language: SUPPORTED_LANGUAGE = 'es',
): PromotionFamilyGrouping {
  const buyXPayYOffers: BuyXPayYOffer[] = [];
  const progressiveTiers: ProgressiveTierOffer[] = [];
  const nthUnits: NthUnitOffer[] = [];

  for (const promo of promotions) {
    switch (promo.discountType) {
      case 'buyXPayY': {
        const config = promo.buyXPayYConfig;
        if (!config) {
          break;
        }
        const { buy, pay } = config;
        // Aligned with engine constraints (`solveOptimalDistribution` requires
        // `buy >= 2 && buy - pay > 0`). Display must not advertise a promo the
        // engine will silently reject.
        if (!buy || buy < 2 || pay == null || pay >= buy) {
          break;
        }
        buyXPayYOffers.push({
          buy,
          pay,
          averagePercentage: Math.floor(((buy - pay) / buy) * 100),
          sourcePromotionId: promo.id,
        });
        break;
      }
      case 'progressive': {
        const tiers = promo.progressiveConfig?.tiers;
        if (!tiers) {
          break;
        }
        for (const tier of tiers) {
          progressiveTiers.push({
            quantity: tier.quantity,
            percentage: tier.percentage,
            sourcePromotionId: promo.id,
          });
        }
        break;
      }
      case 'nthUnit': {
        const config = promo.nthUnitConfig;
        // Aligned with engine constraints (`calculateNthUnitForAggregate`
        // requires `nthUnit >= 2 && discountPercentage > 0`).
        if (!config?.nthUnit || config.nthUnit < 2 || !config.discountPercentage) {
          break;
        }
        nthUnits.push({
          nthUnit: config.nthUnit,
          discountPercentage: config.discountPercentage,
          ordinal: formatFeminineOrdinal(config.nthUnit, language),
          sourcePromotionId: promo.id,
        });
        break;
      }
    }
  }

  buyXPayYOffers.sort((a, b) => a.buy - b.buy);
  progressiveTiers.sort((a, b) => a.quantity - b.quantity);
  nthUnits.sort((a, b) => a.nthUnit - b.nthUnit);

  return { buyXPayYOffers, progressiveTiers, nthUnits };
}

/**
 * Consolidates buy-X-pay-Y offers so each distinct `buy` (N) appears at most once,
 * keeping the most generous offer (lowest `pay`). Order-independent: when two
 * offers tie on `(buy, pay)`, the one with the lexicographically smaller
 * `sourcePromotionId` wins (stable across input permutations).
 */
export function mergeBuyXPayY(buyXPayYOffers: BuyXPayYOffer[]): BuyXPayYOffer[] {
  const bestByBuy = new Map<number, BuyXPayYOffer>();
  for (const offer of buyXPayYOffers) {
    const existing = bestByBuy.get(offer.buy);
    if (
      !existing ||
      offer.pay < existing.pay ||
      (offer.pay === existing.pay &&
        offer.sourcePromotionId.localeCompare(existing.sourcePromotionId) < 0)
    ) {
      bestByBuy.set(offer.buy, offer);
    }
  }
  return [...bestByBuy.values()].sort((a, b) => a.buy - b.buy);
}

/**
 * Consolidates progressive tiers into the minimum non-decreasing ladder.
 * Keep the max % per quantity, then walk ascending and drop any tier
 * whose % does not exceed the running max (i.e. dominated by a smaller-quantity tier).
 * Order-independent.
 */
export function mergeProgressiveTiers(tiers: ProgressiveTierOffer[]): ProgressiveTierOffer[] {
  const bestByQuantity = new Map<number, ProgressiveTierOffer>();
  for (const tier of tiers) {
    const existing = bestByQuantity.get(tier.quantity);
    if (
      !existing ||
      tier.percentage > existing.percentage ||
      (tier.percentage === existing.percentage &&
        tier.sourcePromotionId.localeCompare(existing.sourcePromotionId) < 0)
    ) {
      bestByQuantity.set(tier.quantity, tier);
    }
  }
  const sorted = [...bestByQuantity.values()].sort((a, b) => a.quantity - b.quantity);

  const result: ProgressiveTierOffer[] = [];
  let runningMax = 0;
  for (const tier of sorted) {
    if (tier.percentage > runningMax) {
      result.push(tier);
      runningMax = tier.percentage;
    }
  }
  return result;
}

/**
 * Applies the per-family merge rules to a `PromotionFamilyGrouping`. The result is
 * the "set efectivo por familia" — the canonical input for headline / tags /
 * cart calculations. Order-independent.
 *
 * N-th unit offers are CYCLICAL alternatives — they are not merged by position
 * (which only made sense under the previous literal-position semantics). They
 * pass through as raw offers; the engine picks the best-of among them at the
 * current cart quantity.
 */
export function mergePromotionFamilies(grouping: PromotionFamilyGrouping): PromotionFamilyGrouping {
  return {
    buyXPayYOffers: mergeBuyXPayY(grouping.buyXPayYOffers),
    progressiveTiers: mergeProgressiveTiers(grouping.progressiveTiers),
    nthUnits: grouping.nthUnits,
  };
}

/**
 * One-shot entry point: groups a raw promotion list into families and merges each
 * family. Use this anywhere the merged, canonical set per family is needed (PDP tags, future cart engine).
 */
export function getEffectivePromotionFamilies(
  promotions: PromotionInput[],
  language: SUPPORTED_LANGUAGE = 'es',
): PromotionFamilyGrouping {
  return mergePromotionFamilies(groupPromotionsByFamily(promotions, language));
}

/**
 * Stable display order for a list of promotions: buyXPayY → nthUnit → progressive.
 * Pure (returns a new array), preserves the original order within the same type.
 */
export function sortPromotionsByType<T extends Pick<PromotionInput, 'discountType'>>(
  promotions: T[],
): T[] {
  return [...promotions].sort(
    (a, b) => DISCOUNT_TYPE_ORDER[a.discountType] - DISCOUNT_TYPE_ORDER[b.discountType],
  );
}

/**
 * Filters promotions whose scope includes the given product, and returns them sorted by type.
 * Optional `isActive` predicate lets the caller further restrict by activity / time window.
 */
export function getPromotionsForProduct<
  T extends { id: string; discountType: DiscountType; scope: { productIds: string[] } },
>(promotions: T[], productId: string, options?: { isActive?: (promo: T) => boolean }): T[] {
  const result: T[] = [];
  for (const promo of promotions) {
    if (promo.scope.productIds.includes(productId)) {
      if (!options?.isActive || options.isActive(promo)) {
        result.push(promo);
      }
    }
  }
  return sortPromotionsByType(result);
}

/**
 * Maps an API/Firestore promotion (rich shape with metadata) to the slim `PromotionInput`
 * used by display components — only `id` + `discountType` + the matching config variant.
 */
export function toPromotionInput(promo: {
  id: string;
  discountType: DiscountType;
  buyXPayYConfig?: BuyXPayYConfig | null;
  progressiveConfig?: ProgressiveConfig | null;
  nthUnitConfig?: NthUnitConfig | null;
}): PromotionInput {
  switch (promo.discountType) {
    case 'buyXPayY':
      return {
        id: promo.id,
        discountType: 'buyXPayY',
        buyXPayYConfig: promo.buyXPayYConfig ?? undefined,
      };
    case 'progressive':
      return {
        id: promo.id,
        discountType: 'progressive',
        progressiveConfig: promo.progressiveConfig ?? undefined,
      };
    case 'nthUnit':
      return {
        id: promo.id,
        discountType: 'nthUnit',
        nthUnitConfig: promo.nthUnitConfig ?? undefined,
      };
  }
}

/**
 * Builds the display input from an `AppliedPromotion` (the realized/applied
 * shape — used both for the live cart's participating promos and for tags on
 * already-placed orders).
 *
 * Progressive is collapsed to the **applied tier**: each entry is already
 * per-product (see `AppliedPromotion.productId`), so `appliedTierPercentage` is
 * the tier that applied for this entry's product. `configSnapshot` holds the
 * full ladder, so without this the tag would show the headline max ("hasta
 * 35%") instead of what actually applied ("15%"). Falls back to the full ladder
 * when the tier is absent (pre-existing orders frozen before the field existed).
 */
export function appliedPromotionToInput(applied: AppliedPromotion): PromotionInput {
  switch (applied.discountType) {
    case 'buyXPayY':
      return {
        id: applied.promotionId,
        discountType: 'buyXPayY',
        buyXPayYConfig: applied.configSnapshot as BuyXPayYConfig,
      };
    case 'progressive':
      return {
        id: applied.promotionId,
        discountType: 'progressive',
        progressiveConfig:
          applied.appliedTierPercentage != null
            ? { tiers: [{ quantity: 0, percentage: applied.appliedTierPercentage }] }
            : (applied.configSnapshot as ProgressiveConfig),
      };
    case 'nthUnit':
      return {
        id: applied.promotionId,
        discountType: 'nthUnit',
        nthUnitConfig: applied.configSnapshot as NthUnitConfig,
      };
  }
}

/**
 * Builds the variant list used by headline computations.
 */
export function extractHeadlineVariants(input: HeadlineProductInput): HeadlineProductVariant[] {
  const items = input.presentationItems;
  if (items?.length) {
    return items.map(item => ({ price: item.price, priceDiscount: item.priceDiscount }));
  }
  return [{ price: input.price, priceDiscount: input.priceDiscount }];
}

/**
 * Price-individual discount % for a single variant, derived from `price` vs `priceDiscount`.
 * Clamps to the [1, 99] range (matches the existing `PriceDiscountPipe` semantics).
 */
export function computeVariantPriceDiscountPct(variant: HeadlineProductVariant): number {
  const { price, priceDiscount } = variant;
  if (!price || !priceDiscount || priceDiscount >= price) {
    return 0;
  }
  const pct = 100 - (priceDiscount * 100) / price;
  if (pct < 1) {
    return 1;
  }
  if (pct > 99) {
    return 99;
  }
  return Math.round(pct);
}

/**
 * Maximum price-individual discount % across the variants of a product.
 */
export function computeMaxPriceDiscountPct(variants: HeadlineProductVariant[]): number {
  let max = 0;
  for (const variant of variants) {
    const pct = computeVariantPriceDiscountPct(variant);
    if (pct > max) {
      max = pct;
    }
  }
  return max;
}

/**
 * Single source of truth for the "HASTA X% OFF" headline of a product.
 *
 * Returns floor(max) across three families:
 *   - Price-individual discount (per variant).
 *   - Bundle (buyXPayY, nthUnit), normalized to AVG % via getPromotionEffectiveDiscount.
 *   - Quantity progressive, taken as the top tier.
 *
 * Never stacks. Uses Math.floor so the headline never over-promises.
 */
export function computeHeadlineDiscount(
  variants: HeadlineProductVariant[],
  promotions: PromotionInput[],
): number {
  const priceDiscountPct = computeMaxPriceDiscountPct(variants);
  let promoMax = 0;
  for (const promo of promotions) {
    const effective = getPromotionEffectiveDiscount(promo);
    if (effective > promoMax) {
      promoMax = effective;
    }
  }
  return Math.floor(Math.max(priceDiscountPct, promoMax));
}

/**
 * Headline-comparable % for a single promotion (used to compare/sort and to feed
 * `computeHeadlineDiscount`). Normalizes each family to its "fuera de contexto" value:
 *   - buyXPayY → `(buy - pay) / buy` (bundle AVG)
 *   - nthUnit  → `discountPercentage / nthUnit` (bundle AVG, never the raw nth-unit %)
 *   - progressive → max tier %
 * Uses `Math.floor` so it never over-promises.
 */
export function getPromotionEffectiveDiscount(promotion: PromotionInput): number {
  switch (promotion.discountType) {
    case 'buyXPayY': {
      const config = promotion.buyXPayYConfig;
      if (!config?.buy || config.buy < 2 || config.pay == null || config.pay >= config.buy) {
        return 0;
      }
      return Math.floor(((config.buy - config.pay) / config.buy) * 100);
    }
    case 'progressive': {
      const tiers = promotion.progressiveConfig?.tiers;
      if (!tiers?.length) {
        return 0;
      }
      return Math.max(...tiers.map(t => t.percentage));
    }
    case 'nthUnit': {
      const config = promotion.nthUnitConfig;
      if (!config?.nthUnit || config.nthUnit < 2 || !config.discountPercentage) {
        return 0;
      }
      return Math.floor(config.discountPercentage / config.nthUnit);
    }
  }
}

export function activePromotedProductIds<
  T extends {
    isActive: boolean;
    conditions?: { period?: PromotionPeriod };
    scope: { productIds: string[] };
  },
>(promotions: T[], now: Date): Set<string> {
  return new Set(
    promotions
      .filter(
        promotion =>
          promotion.isActive && isPromotionWithinPeriod(promotion.conditions?.period, now),
      )
      .flatMap(promotion => promotion.scope.productIds),
  );
}
